import React, { useContext, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
    Box,
    Text,
    Icon,
    VStack,
    HStack,
    Input,
    Flex,
    CloseButton,
    Tag,
    TagLabel,
    Spinner,
    useToast,
    Image
} from "@chakra-ui/react";
import { FaFile } from "react-icons/fa";
import { v4 as uuidv4 } from 'uuid';
import { CheckCircleIcon } from "@chakra-ui/icons";
import { PDFDocument } from 'pdf-lib';
import { ModalBox } from "../ModalBox";
import { uploadWithSasToken, extractDetailsFromUrl, validateImage, extractTextFromFile } from "../../utils/helper";
import { PublisherAndUserContext } from "../../contexts/PublisherAndUserContext";
import { ImageCropper } from "../ImageCropper";

const fileTypes = {
    images: "image/*",
    audios: "audio/*",
    pdfs: "application/pdf",
    ppts: ".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
};

const uploadDesc = {
    images: "When you upload an image, we automatically extract text from it to help you create interactive EdDocs effortlessly.",
    audios: "When you upload an audio file, we extract its narration to build EdDocs with detailed, interactive content.",
    pdfs: "Upon uploading a PDF, we extract its text content to generate structured EdDocs, ready for learning experiences.",
    ppts: "Ensure that your Speaker Notes are well-prepared before uploading. These notes are automatically used to generate voiceovers for video creation, seamlessly integrated with EdDoc."
};

export const UploadResourceModal = ({ isOpen, filterType, onClose, isLogo = false }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [isCropperOpen, setIsCropperOpen] = useState(false);
    const [invalidFiles, setInvalidFiles] = useState([]);
    const { publisher } = useContext(PublisherAndUserContext);
    const toast = useToast();
    const apolloClient = useApolloClient();
    const maxFiles = isLogo ? 1 : 10;

    const fileTypePath = {
        "pdf": "pdf/parse",
        "video": "video/extract-text",
        "image": "image/extract-text",
        "audio": "audio/extract-text"
    }

    const getFileData = (file) => {
        const data = {
            "pdf": {
                pdfUrl: file.url,
                uuid: file.uuid,
                containerPath: `${publisher.id}/coursecontent`,
                publisherId: publisher?.id
            },
            "image": {
                imageUrl: file.url,
                uuid: file.uuid,
                publisherId: publisher?.id
            },
            "audio": {
                audioUrl: file.url,
                uuid: file.uuid,
                publisherId: publisher?.id
            }
        }
        return JSON.stringify(data[file.type.includes("pdf") ? file.type.split('/')[1] : file.type.split('/')[0]])
    }
    function renameFile(file, newName) {
        return new File([file], newName, {
          type: file.type,
          lastModified: file.lastModified,
        });
    }
      
    const handleFileChange = async (event) => {
        let newFiles = Array.from(event.target.files || []);
        newFiles = newFiles.map((file, index) =>
            renameFile(file, file.name.replace(/ /g, ''))
        );
        const maxSizeMB = filterType === "images" ? 2 : 5; // Maximum file size in MB

        // Filter files that exceed size limit
        const validFiles = newFiles.filter(file => file.size <= maxSizeMB * 1024 * 1024);

        if (validFiles.length !== newFiles.length) {
            toast({
                position: "top-right",
                description: `Some files were not added because they exceed ${maxSizeMB}MB.`,
                status: "info",
                duration: 4000,
                isClosable: true,
            });
            return true
        }
        if (isLogo) {
            try {
                const img = await validateImage(newFiles[0], 450, 200, 1200, 1200);
            } catch (e) {
                toast({
                    position: "top-right",
                    title: e,
                    status: "error",
                    duration: 4000,
                    isClosable: true
                });
                return true
            }
            setIsCropperOpen(true)
        }

        setSelectedFiles((prev) => {
            const totalFiles = prev.length + validFiles.length;
            if (totalFiles > maxFiles) {
                toast({
                    position: "top-right",
                    title: `You can only upload up to ${maxFiles} files.`,
                    status: "info",
                    duration: 4000,
                    isClosable: true
                });
                return prev;
            }
            return [...prev, ...validFiles];
        });
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const onCloseModal = () => {
        setIsUploading(false);
        setSelectedFiles([]);
        setUploadProgress({});
        setInvalidFiles([]);
        onClose();
    };

    const uploadFile = async (file, index) => {
        try {
            setUploadProgress((prev) => ({ ...prev, [index]: 0 }));
            const uuid = uuidv4()
            const sasUrl = await uploadWithSasToken(publisher?.id, filterType, file, uuid)
            if (!sasUrl) return alert("SAS URL is missing");
            setUploadProgress((prev) => ({ ...prev, [index]: 100 }));
            const url = sasUrl.split("?")[0]
            const { id, folderName, fileName } = extractDetailsFromUrl(url)
            const newResource = {
                key: `${id}/${folderName}/${fileName}`,
                url: url,
                type: file.type,
                size: file.size,
                name: file.name,
                uuid: uuid
            };
            const path = fileTypePath[newResource.type.includes("pdf") ? newResource.type.split('/')[1] : newResource.type.split('/')[0]]
            const data = getFileData(newResource)
            if(folderName !== "ppts") extractTextFromFile(path, data)
            if (folderName === "images") {
                apolloClient.cache.modify({
                    fields: {
                        edshopifyImageResources(existingEdshopifyImageResourcesRefs = {}, { readField }) {
                            const updatedResources = [
                                newResource,
                                ...existingEdshopifyImageResourcesRefs.resources,
                            ];

                            return {
                                ...existingEdshopifyImageResourcesRefs,
                                resources: updatedResources,
                            };
                        },
                    },
                });
            }
            if (folderName === "audios") {
                apolloClient.cache.modify({
                    fields: {
                        edshopifyAudioResources(existingEdshopifyAudioResourcesRefs = {}, { readField }) {
                            const updatedResources = [
                                newResource,
                                ...existingEdshopifyAudioResourcesRefs.resources,
                            ];

                            return {
                                ...existingEdshopifyAudioResourcesRefs,
                                resources: updatedResources,
                            };
                        },
                    },
                });
            }
            if (folderName === "pdfs") {
                apolloClient.cache.modify({
                    fields: {
                        edshopifyPdfResources(existingEdshopifyPdfResourcesRefs = {}, { readField }) {
                            const updatedResources = [
                                newResource,
                                ...existingEdshopifyPdfResourcesRefs.resources,
                            ];

                            return {
                                ...existingEdshopifyPdfResourcesRefs,
                                resources: updatedResources,
                            };
                        },
                    },
                });
            }
            if (folderName === "ppts") {
                apolloClient.cache.modify({
                    fields: {
                        edshopifyPptResources(existingEdshopifyPptResourcesRefs = {}, { readField }) {
                            const updatedResources = [
                                newResource,
                                ...existingEdshopifyPptResourcesRefs.resources,
                            ];

                            return {
                                ...existingEdshopifyPptResourcesRefs,
                                resources: updatedResources,
                            };
                        },
                    },
                });
            }
        } catch (error) {
            console.error("Upload failed:", error);
            onCloseModal();
            toast({
                position: "top-right",
                title: `Error uploading ${file.name}`,
                status: "error",
                duration: 2000,
                isClosable: true
            });
        }
    };

    const handleUploadAll = async () => {
        setIsUploading(true);
        let invalidPdfFiles = [];
      
        if (filterType === "pdfs") {
            await Promise.all(
                selectedFiles.map(async (file) => {
                    try {
                        const arrayBuffer = await file.arrayBuffer();
                        const pdfDoc = await PDFDocument.load(arrayBuffer);
                        const pageCount = pdfDoc.getPageCount();

                        if (pageCount > 50) {
                            setInvalidFiles([...invalidFiles, file])
                            invalidPdfFiles.push(file);
                        }
                    } catch (error) {
                        console.error("Error reading file:", file.name, error);
                    }
                })
            );
            if (invalidPdfFiles.length > 0) {
                setIsUploading(false);
                toast({
                    position: "top-right",
                    description: `Failed to add. One or more PDFs exceed the 50-page limit.`,
                    status: "error",
                    duration: 6000,
                    isClosable: true,
                });
                return;
            } else {
                await Promise.allSettled(selectedFiles.map((file, index) => uploadFile(file, index)));
                onCloseModal();
            }
        } else {
            await Promise.allSettled(selectedFiles.map((file, index) => uploadFile(file, index)));
            onCloseModal();
        }
      };
          

    const handleCropComplete = async (croppedBlob) => {
        try {
          const file = new File([croppedBlob], selectedFiles[0].name, { type: croppedBlob.type })
          console.log({
            croppedFile : file,
            originalFile: selectedFiles[0]
          })
          setSelectedFiles([file])
          setIsCropperOpen(false)
        } catch (e) {
          toast({
            position: "top-right",
            description: e,
            status: "error",
            duration: 4000,
            isClosable: true
         });
        }
      };

    const name = filterType === "images" ? "Image" : filterType === "pdfs" ? "Pdf" : filterType === "ppts" ? "PPt" : "Audio"

    return (
        <>
            <ModalBox
                title={`Upload ${name}`}
                size="xl"
                description={
                    <>
                        {/* Drag-and-Drop Area */}
                        {!isUploading && 
                        <>
                            <Text margin="0 0 10px">{uploadDesc[filterType]}</Text>
                            {filterType === "pdfs" ? <Text as="span" fontSize="sm" color="gray.500">PDF page limit - 50</Text> : null}
                            <Box
                                w="full"
                                h="200px"
                                borderWidth="2px"
                                borderStyle="dashed"
                                borderColor="gray.300"
                                borderRadius="lg"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexDirection="column"
                                bg="gray.50"
                                margin="0"
                                cursor={selectedFiles.length >= maxFiles ? "not-allowed" : "pointer"}
                                _hover={selectedFiles.length < maxFiles ? { bg: "gray.100" } : {}}
                            >
                                <Input
                                    type="file"
                                    multiple
                                    accept={fileTypes[filterType]}
                                    display="none"
                                    id="file-upload"
                                    onChange={handleFileChange}
                                    disabled={selectedFiles.length >= maxFiles}
                                />
                                <label htmlFor="file-upload">
                                    <Flex direction="column" align="center">
                                        <Icon as={FaFile} boxSize={10} color="gray.500" m={3}/>
                                        {/* <Text mt={2} fontSize="lg" fontWeight="bold" color="gray.600">
                                            Drag and drop files here
                                        </Text> */}
                                        <Text fontSize="md" fontWeight="bold" color="gray.500">
                                            Click to upload
                                        </Text>
                                        {selectedFiles.length >= maxFiles && (
                                            <Text color="red.500" fontSize="sm" mt={1}>
                                                Maximum of {maxFiles} files reached.
                                            </Text>
                                        )}
                                    </Flex>
                                </label>
                            </Box>
                        </>
                        }

                        {/* Selected Files List */}
                        {selectedFiles.length > 0 && (
                            <VStack spacing={3} mt={4} align="stretch">
                                {selectedFiles.map((file, index) => (
                                    <HStack
                                        key={index}
                                        p={3}
                                        borderWidth="1px"
                                        borderRadius="md"
                                        borderColor="gray.200"
                                        bg="gray.50"
                                        justify="space-between"
                                        align="center"
                                    >
                                        <Text isTruncated width="80%" fontSize="sm" color={invalidFiles.findIndex(f => f.name === file.name) > -1 ? "red" : "black.400"}>
                                            {file.name}
                                        </Text>
                                        {/* Show Upload Progress */}
                                        <Flex width="20%" alignItems="center" justifyContent="flex-end">
                                            {uploadProgress[index] && uploadProgress[index] < 100 ? (
                                                <Tag variant='solid' colorScheme='yellow'>
                                                    <Spinner
                                                        thickness='4px'
                                                        speed='0.65s'
                                                        emptyColor='gray.200'
                                                        color='blue.500'
                                                        size='xs'
                                                    />
                                                    <TagLabel>Uploading</TagLabel>
                                                </Tag>
                                            ) : uploadProgress[index] === 100 ? (
                                                <CheckCircleIcon color="green.500" />
                                            ) : isUploading ? <Image src="/images/upload.gif" alt="uploading" /> : null}
                                            {!isUploading && <CloseButton size="sm" onClick={() => handleRemoveFile(index)} />}
                                        </Flex>
                                    </HStack>
                                ))}
                            </VStack>
                        )}
                    </>
                }
                isOpen={isOpen}
                onClose={onCloseModal}
                isFooter={true}
                cancelButtonText="Cancel"
                confirmButtonText="Upload"
                onCancel={onCloseModal}
                isWorking={isUploading}
                loading={isUploading}
                loadingText="Uploading"
                onConfirm={handleUploadAll}
            />
            {isCropperOpen && selectedFiles[0] && isLogo && <ImageCropper 
                file={selectedFiles[0]}
                isOpen={isCropperOpen}
                onClose={() => setIsCropperOpen(false)}
                onCropComplete={handleCropComplete}
            />}
        </>
    );
};
